/**
 * Financial & Ops screen.
 *
 * Data sources
 *   - GET /admin/metrics/financial   (windowed flows + snapshots + aging)
 *   - GET /admin/metrics/reconciliation
 *
 * CSV export streams the daily deposit/withdrawal/net-flow trend via the
 * new format=csv branch on /admin/metrics/financial.
 */

import { api, ApiError } from '../../api.js';
import { toast } from '../../components/toast.js';
import {
  downloadCsv,
  errorState,
  escape,
  fetchOrNull,
  fmtInt,
  fmtKobo,
  fromToQuery,
  kpi,
  mountDivergingBars,
  mountSortableTable,
  skeletonGrid,
  snapshotKpi,
} from './common.js';

export async function renderFinancial(host, ctx) {
  host.innerHTML = skeletonGrid(6);
  try {
    const [fin, recon] = await Promise.all([
      api(`/admin/metrics/financial${fromToQuery(ctx.range)}`),
      fetchOrNull('/admin/metrics/reconciliation'),
    ]);
    host.innerHTML = _render(fin, recon, ctx);
    _mountFlowChart(host, fin);
    _mountTables(host, fin, recon);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      ctx.navigate('/login');
      return;
    }
    toast.fromError(err);
    host.innerHTML = errorState(err);
  }
}

export function exportCsv({ range }) {
  const qs = fromToQuery(range);
  const path = qs ? `/admin/metrics/financial${qs}&format=csv` : '/admin/metrics/financial?format=csv';
  downloadCsv(path, `triviana_financial_${range.start}_${range.end}.csv`);
}

function _render(fin, recon, ctx) {
  const snap = [
    snapshotKpi({
      label: 'Platform revenue balance',
      value: fmtKobo(fin.platform_revenue_kobo),
      hint: 'available · house',
    }),
    snapshotKpi({
      label: 'Lifetime rake',
      value: fmtKobo(fin.rake_lifetime_kobo),
      hint: 'all-time house cut',
    }),
    snapshotKpi({
      label: 'Wallet liability (owed to users)',
      value: fmtKobo(fin.wallet_liability_kobo),
      hint: 'sum of user balances',
    }),
  ].join('');

  const flows = [
    kpi({
      label: 'Deposits',
      value: fmtKobo(fin.deposits?.totals?.succeeded_kobo),
      hint: 'succeeded · window',
    }),
    kpi({
      label: 'Withdrawals',
      value: fmtKobo(fin.withdrawals?.totals?.succeeded_kobo),
      hint: 'succeeded · window',
    }),
    kpi({
      label: 'Net flow',
      value: fmtKobo(fin.net_flow_kobo),
      hint: 'deposits − withdrawals',
    }),
    kpi({
      label: 'Live payouts',
      value: fmtKobo(fin.payouts?.live_payout_kobo),
      hint: 'live-trivia prizes',
    }),
    kpi({
      label: 'Contest settle',
      value: fmtKobo(fin.payouts?.contest_settle_winner_kobo),
      hint: 'duel prize payouts',
    }),
  ].join('');

  const flowChart = `<div id="adm2-financial-flow"></div>`;

  const wstatus = fin.withdrawals?.totals || {};
  const wRows = [
    { name: 'Pending', count: wstatus.pending, kobo: null, cls: 'w' },
    { name: 'Succeeded', count: wstatus.succeeded, kobo: wstatus.succeeded_kobo, cls: 'g' },
    { name: 'Failed', count: wstatus.failed, kobo: null, cls: 'r' },
    { name: 'Cancelled', count: wstatus.cancelled, kobo: null, cls: '' },
  ]
    .map(
      (w) => `
    <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;padding:11px 13px;border:1px solid var(--a-border);background:var(--a-panel-2);border-radius:10px;margin-bottom:8px;">
      <span style="font:600 12px 'IBM Plex Sans',sans-serif;color:var(--a-ink-2);">${escape(w.name)}</span>
      <span style="display:flex;gap:16px;">
        <span style="font:500 12px 'IBM Plex Mono',monospace;color:var(--a-muted);">${escape(fmtInt(w.count))}</span>
        <span style="font:500 12px 'IBM Plex Mono',monospace;color:var(--a-ink-2);">${escape(w.kobo == null ? '—' : fmtKobo(w.kobo))}</span>
      </span>
    </div>
  `,
    )
    .join('');

  const aging = fin.pending_withdrawal_aging || { buckets: {} };
  const ageMax = Math.max(1, aging.buckets?.lt_1d || 0, aging.buckets?.['1d_to_7d'] || 0, aging.buckets?.gt_7d || 0);
  const agingRows = [
    ['< 1 day', aging.buckets?.lt_1d || 0, 'var(--a-ok)'],
    ['1–7 days', aging.buckets?.['1d_to_7d'] || 0, 'var(--a-warn)'],
    ['> 7 days', aging.buckets?.gt_7d || 0, 'var(--a-bad)'],
  ]
    .map(
      ([label, count, color]) => `
    <div style="margin-bottom:14px;">
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:6px;">
        <span style="font:500 12px 'IBM Plex Sans',sans-serif;color:var(--a-ink-2);">${escape(label)}</span>
        <span style="font:500 12px 'IBM Plex Mono',monospace;color:var(--a-muted);">${escape(fmtInt(count))} pending</span>
      </div>
      <div class="adm2-bar-track">
        <div class="adm2-bar-fill" style="width:${(100 * count) / ageMax}%;background:${color};"></div>
      </div>
    </div>
  `,
    )
    .join('');

  const reconPanel = _reconPanel(recon, ctx);

  return `
    <div style="display:flex;align-items:center;gap:10px;margin:0 0 12px;">
      <span style="font:600 12px 'IBM Plex Sans',sans-serif;letter-spacing:.04em;text-transform:uppercase;color:var(--a-warm-ink-2);">Live snapshots</span>
      <span style="font:400 11px 'IBM Plex Sans',sans-serif;color:var(--a-warm-ink-2);">— current balances, not affected by date range</span>
      <span style="flex:1;height:1px;background:var(--a-warm-bd);"></span>
    </div>
    <div class="adm2-grid" style="grid-template-columns:repeat(auto-fit,minmax(245px,1fr));">${snap}</div>

    ${reconPanel}

    <div class="adm2-section-label">Flows · windowed</div>
    <div class="adm2-grid" style="grid-template-columns:repeat(auto-fit,minmax(186px,1fr));">${flows}</div>

    <div class="adm2-panel" style="margin-top:16px;">
      <div style="display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:12px;">
        <div>
          <div class="adm2-panel-title">Deposit / withdrawal · last 14 buckets</div>
          <div class="adm2-panel-sub">from daily_flow[] · deposits above the axis, withdrawals below</div>
        </div>
        <div class="adm2-legend">
          <span><i style="background:var(--a-ok);"></i>Deposits</span>
          <span><i style="background:#E08177;"></i>Withdrawals</span>
        </div>
      </div>
      <div style="margin-top:16px;">${flowChart}</div>
    </div>

    <div class="adm2-two">
      <div class="adm2-panel">
        <div class="adm2-panel-title">Deposits by provider</div>
        <div class="adm2-panel-sub">click a header to sort · counts by state · ₦ succeeded</div>
        <div id="fin-providers" style="margin-top:14px;"></div>
      </div>

      <div class="adm2-panel">
        <div class="adm2-panel-title">Withdrawals by status</div>
        <div class="adm2-panel-sub">counts · ₦ succeeded</div>
        <div style="margin-top:14px;">${wRows}</div>
      </div>
    </div>

    <div class="adm2-panel" style="margin-top:16px;">
      <div style="display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:10px;">
        <div>
          <div class="adm2-panel-title">Pending-withdrawal aging</div>
          <div class="adm2-panel-sub">buckets · count + oldest age</div>
        </div>
        <div style="text-align:right;">
          <div style="font:600 22px 'IBM Plex Mono',monospace;color:var(--a-bad);">${escape(
            aging.oldest_age_hours == null ? '—' : `${aging.oldest_age_hours} hrs`,
          )}</div>
          <div style="font:500 10px 'IBM Plex Sans',sans-serif;color:var(--a-faint);">oldest pending</div>
        </div>
      </div>
      <div style="margin-top:16px;">${agingRows}</div>
    </div>
  `;
}

function _reconPanel(recon, ctx) {
  if (!recon) {
    return `<div class="adm2-recon ok" style="margin-top:18px;"><div class="adm2-recon-dot"></div>
      <div><div class="adm2-recon-title">Ledger status</div>
      <div class="adm2-recon-sub">reconciliation not yet loaded</div></div></div>`;
  }
  const balanced = !!recon.ok;
  const cls = balanced ? 'ok' : 'bad';
  const hasDrift = (recon.account_drift || []).length > 0;

  return `
    <div class="adm2-panel" style="margin-top:18px;padding:0;overflow:hidden;">
      <div class="adm2-recon ${cls}" style="border-radius:0;border:none;border-bottom:1px solid ${balanced ? 'var(--a-ok-bd)' : 'var(--a-bad-bd)'};">
        <div class="adm2-recon-dot"></div>
        <div style="flex:1;min-width:200px;">
          <div class="adm2-recon-title">${escape(balanced ? 'Ledger reconciled' : 'Ledger out of balance')}</div>
          <div class="adm2-recon-sub">${escape(
            balanced
              ? `debits = credits · global diff ${fmtKobo(0)} · ${fmtInt(recon.unbalanced_txns_count)} unbalanced txns`
              : `global diff ${fmtKobo(recon.global_diff_kobo)} · ${fmtInt(recon.unbalanced_txns_count)} unbalanced txns`,
          )}</div>
        </div>
      </div>
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(155px,1fr));gap:14px;padding:20px 22px;">
        ${_reconStat('Total debits', fmtKobo(recon.global_dr_kobo), 'var(--a-ink)')}
        ${_reconStat('Total credits', fmtKobo(recon.global_cr_kobo), 'var(--a-ink)')}
        ${_reconStat('Global diff', fmtKobo(recon.global_diff_kobo), balanced ? 'var(--a-ok)' : 'var(--a-bad)')}
        ${_reconStat('Unbalanced txns', fmtInt(recon.unbalanced_txns_count), balanced ? 'var(--a-ok)' : 'var(--a-bad)')}
      </div>
      ${
        hasDrift
          ? `
        <div style="padding:0 22px 20px;">
          <div style="font:600 12px 'IBM Plex Sans',sans-serif;color:var(--a-ink-2);margin-bottom:10px;">Account drift · cached vs derived balance · click a header to sort</div>
          <div id="fin-drift" style="border:1px solid var(--a-border);border-radius:11px;overflow:hidden;"></div>
        </div>`
          : ''
      }
    </div>
  `;
}

function _mountFlowChart(host, fin) {
  const chartHost = host.querySelector('#adm2-financial-flow');
  const days = (fin.daily_flow || []).slice(-14);
  const bars = days.map((r) => ({
    up: r.deposits_kobo || 0,
    down: r.withdrawals_kobo || 0,
    label: r.date,
  }));
  mountDivergingBars(chartHost, {
    bars,
    height: 184,
    valueFmt: (v) => fmtKobo(v),
    upLabel: 'Deposits',
    downLabel: 'Withdrawals',
    emptyMessage: 'No flow data in range.',
  });
}

function _mountTables(host, fin, recon) {
  const providersHost = host.querySelector('#fin-providers');
  if (providersHost) {
    mountSortableTable(providersHost, {
      columns: [
        {
          key: 'provider',
          label: 'Provider',
          text: true,
          get: (r) => (r.provider || '').toLowerCase(),
          cell: (r) => escape(r.provider || '—'),
        },
        {
          key: 'pending',
          label: 'Pending',
          right: true,
          get: (r) => Number(r.pending || 0),
          cell: (r) => `<span style="color:var(--a-warn);">${escape(fmtInt(r.pending))}</span>`,
          className: 'mono',
        },
        {
          key: 'succeeded',
          label: 'Succeeded',
          right: true,
          get: (r) => Number(r.succeeded || 0),
          cell: (r) => `<span style="color:var(--a-ok);">${escape(fmtInt(r.succeeded))}</span>`,
          className: 'mono',
        },
        {
          key: 'failed',
          label: 'Failed',
          right: true,
          get: (r) => Number(r.failed || 0),
          cell: (r) => `<span style="color:var(--a-bad);">${escape(fmtInt(r.failed))}</span>`,
          className: 'mono',
        },
        {
          key: 'succeeded_kobo',
          label: '₦ succ',
          right: true,
          get: (r) => Number(r.succeeded_kobo || 0),
          cell: (r) => escape(fmtKobo(r.succeeded_kobo)),
          className: 'mono',
        },
      ],
      rows: fin.deposits?.by_provider || [],
      defaultSort: { key: 'succeeded_kobo', dir: 'desc' },
      minWidth: 560,
      emptyMessage: 'No deposits in this window.',
      pageSizeKey: 'financial.providers',
    });
  }

  const driftHost = host.querySelector('#fin-drift');
  if (driftHost && recon && (recon.account_drift || []).length) {
    mountSortableTable(driftHost, {
      columns: [
        {
          key: 'account',
          label: 'Account',
          text: true,
          get: (r) => `${r.account_type || ''}·${r.account_id || ''}`.toLowerCase(),
          cell: (r) => `${escape(r.account_type)} · ${escape((r.account_id || '').slice(0, 8))}`,
          className: 'mono',
        },
        {
          key: 'cached',
          label: 'Cached',
          right: true,
          get: (r) => Number(r.cached_kobo || 0),
          cell: (r) => escape(fmtKobo(r.cached_kobo)),
          className: 'mono',
        },
        {
          key: 'derived',
          label: 'Derived',
          right: true,
          get: (r) => Number(r.derived_kobo || 0),
          cell: (r) => escape(fmtKobo(r.derived_kobo)),
          className: 'mono',
        },
        {
          key: 'diff',
          label: 'Diff',
          right: true,
          get: (r) => Math.abs(Number((r.derived_kobo || 0) - (r.cached_kobo || 0))),
          cell: (r) => {
            const diff = (r.derived_kobo || 0) - (r.cached_kobo || 0);
            return `<span class="adm2-chip ${diff === 0 ? 'g' : 'r'}">${escape(fmtKobo(diff))}</span>`;
          },
        },
      ],
      rows: recon.account_drift || [],
      defaultSort: { key: 'diff', dir: 'desc' },
      minWidth: 560,
      emptyMessage: 'No drift.',
      pageSizeKey: 'financial.drift',
    });
  }
}

function _reconStat(label, value, color) {
  return `
    <div style="border:1px solid var(--a-border);border-radius:11px;padding:14px 15px;background:var(--a-panel-2);">
      <div style="font:600 10px 'IBM Plex Sans',sans-serif;letter-spacing:.05em;text-transform:uppercase;color:var(--a-muted);">${escape(label)}</div>
      <div style="font:600 19px 'IBM Plex Mono',monospace;margin-top:9px;color:${escape(color)};">${escape(value)}</div>
    </div>
  `;
}
